import { useMemo, useState } from "react";
import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Plus,
  Search,
  Send,
  X,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies } from "@/hooks/useCompanies";
import { useUsers } from "@/hooks/useUser";
import { useCreateShifts, useFetchShifts, usePublishShifts } from "@/hooks/useShifts";
import { useOvertime } from "@/hooks/useOvertime";
import type { OvertimeEmployee } from "@/services/overtime.service";
import type { Shift, ShiftCreateInput } from "@/types/shift.type";
import type { UserType } from "@/types/user.type";
import { ShiftFormDialog } from "@/components/planning/ShiftFormDialog";
import { EmployeeOvertimeDialog, type OvertimeDialogEmployee } from "@/components/planning/EmployeeOvertimeDialog";
import { exportPlanningToPDF } from "@/lib/planning-pdf";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

// ─── Palette de couleurs pour les shifts (1 couleur par employé) ──────────────

const SHIFT_COLORS = [
  { bg: "bg-sky-100",     text: "text-sky-800",     border: "border-sky-200",     hover: "hover:bg-sky-200"     },
  { bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-200",   hover: "hover:bg-amber-200"   },
  { bg: "bg-violet-100",  text: "text-violet-800",  border: "border-violet-200",  hover: "hover:bg-violet-200"  },
  { bg: "bg-rose-100",    text: "text-rose-800",    border: "border-rose-200",    hover: "hover:bg-rose-200"    },
  { bg: "bg-teal-100",    text: "text-teal-800",    border: "border-teal-200",    hover: "hover:bg-teal-200"    },
  { bg: "bg-orange-100",  text: "text-orange-800",  border: "border-orange-200",  hover: "hover:bg-orange-200"  },
  { bg: "bg-blue-100",    text: "text-blue-800",    border: "border-blue-200",    hover: "hover:bg-blue-200"    },
  { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200", hover: "hover:bg-emerald-200" },
  { bg: "bg-pink-100",    text: "text-pink-800",    border: "border-pink-200",    hover: "hover:bg-pink-200"    },
  { bg: "bg-indigo-100",  text: "text-indigo-800",  border: "border-indigo-200",  hover: "hover:bg-indigo-200"  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Détecte un congé / absence à partir de la note du shift */
function getLeaveStyle(note: string | null): string | null {
  if (!note) return null;
  const n = note.toLowerCase();
  if (n.includes("congé") || n.includes("conge") || n.includes("vacance")) {
    return "bg-cyan-100 text-cyan-800 border-cyan-200";
  }
  if (
    n.includes("maladie") ||
    n.includes("arrêt") ||
    n.includes("arret") ||
    n.includes("accident")
  ) {
    return "bg-gray-100 text-gray-500 border-gray-200";
  }
  return null;
}

// Génère les 7 jours de la semaine (lundi -> dimanche)
function getWeekDays(currentDate: Date): Date[] {
  const monday = startOfWeek(currentDate, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

// Format "09:00"
function formatTime(iso: string) {
  return format(new Date(iso), "HH:mm");
}

// Durée nette d'un shift en minutes (fin - début - pause)
// Ne compte que les shifts "PLANNED" (les "CANCELLED" renvoient 0)
function getShiftMinutes(shift: Shift): number {
  if (shift.status !== "PLANNED") return 0;
  const start = new Date(shift.startTime).getTime();
  const end = new Date(shift.endTime).getTime();
  const diffMin = Math.floor((end - start) / 60000);
  return Math.max(0, diffMin - (shift.breakMinutes ?? 0));
}

// Format lisible d'une durée en minutes → "40h30" ou "8h" ou "0h"
function formatDuration(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h${String(minutes).padStart(2, "0")}`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const PlanningPage = () => {
  const { user } = useAuth();
  const isSuperuser = user?.role === "SUPERUSER";

  // Entreprise à afficher
  //  - ADMIN    : sa propre entreprise (user.companyId)
  //  - SUPERUSER : celle qu'il sélectionne dans le dropdown (selectedCompanyId)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const effectiveCompanyId = isSuperuser ? selectedCompanyId : user?.companyId;

  // Liste des entreprises (uniquement pour SUPERUSER, pour remplir le Select)
  const { data: companiesData } = useCompanies(
    { limit: 100, isActive: true },
    { enabled: isSuperuser }
  );
  const companies = companiesData?.companies ?? [];

  // Semaine affichée (on stocke le lundi)
  const [currentWeek, setCurrentWeek] = useState<Date>(() => new Date());
  const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek]);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

  // Recherche d'employé
  const [search, setSearch] = useState("");

  // Dialog création / édition
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [dialogEmployeeId, setDialogEmployeeId] = useState<string | undefined>(
    undefined
  );
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [showDateFieldInDialog, setShowDateFieldInDialog] = useState(false);


  // Données : employés de la société
  //  - ADMIN : le backend filtre auto par sa companyId (pas besoin de le passer)
  //  - SUPERUSER : on doit passer explicitement companyId pour cibler l'entreprise choisie
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers(
    {
      limit: 500,
      role: "EMPLOYEE",
      isActive: true,
      companyId: isSuperuser ? effectiveCompanyId || undefined : undefined,
    },
    { enabled: !!effectiveCompanyId } 
  );
  const employees: UserType[] = useMemo(() => {
    const sorted = [...(usersData?.users ?? [])].sort((a, b) => {
      const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
      const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB, "fr");
    });
    if (!search.trim()) return sorted;
    const q = search.trim().toLowerCase();
    return sorted.filter((e) =>
      `${e.firstName ?? ""} ${e.lastName ?? ""}`.toLowerCase().includes(q)
    );
  }, [usersData, search]);

  // Données : shifts de la semaine
  const {
    data: shiftsData,
    isLoading: shiftsLoading,
    error: shiftsError,
  } = useFetchShifts(
    {
      from: format(weekStart, "yyyy-MM-dd"),
      to: format(addDays(weekEnd, 1), "yyyy-MM-dd"), // "to" exclusif côté backend
      companyId: isSuperuser ? effectiveCompanyId || undefined : undefined,
    },
    { enabled: !!effectiveCompanyId }
  );
  const shifts: Shift[] = shiftsData?.shifts ?? [];

  // Helpers pour retrouver les shifts d'une case
  // const shiftsByCell = useMemo(() => {
  //   const map = new Map<string, Shift[]>();
  //   for (const s of shifts) {
  //     const displayDate = s.workDate ? new Date(s.workDate) : new Date(s.startTime);
  //     const key = `${s.employeeId}__${format(new Date(displayDate), "yyyy-MM-dd")}`;
  //     const list = map.get(key) ?? [];
  //     list.push(s);
  //     map.set(key, list);
  //   }
  //   // Trier par startTime dans chaque cellule
  //   for (const list of map.values()) {
  //     list.sort(
  //       (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  //     );
  //   }
  //   return map;
  // }, [shifts]);
  const shiftsByCell = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) {
      const displayDate = s.workDate ? new Date(s.workDate) : new Date(s.startTime);
      const key = `${s.employeeId}__${format(displayDate, "yyyy-MM-dd")}`;
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
  
    for (const list of map.values()) {
      list.sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
    }

    return map;
  }, [shifts]);

  // Retourne les shifts d'une cellule (employé + date)
  const getCellShifts = (employeeId: string, day: Date) =>
    shiftsByCell.get(`${employeeId}__${format(day, "yyyy-MM-dd")}`) ?? [];

  // Total de minutes planifiées par employé sur la semaine affichée
  const totalMinutesByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of shifts) {
      const current = map.get(s.employeeId) ?? 0;
      map.set(s.employeeId, current + getShiftMinutes(s));
    }
    return map;
  }, [shifts]);

  // Actions
  const handleOpenCreateDialog = (employeeId: string, day: Date) => {
    setEditingShift(null);
    setDialogEmployeeId(employeeId);
    setDialogDate(format(day, "yyyy-MM-dd"));
    setShowDateFieldInDialog(true);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (shift: Shift) => {
    setEditingShift(shift);
    setShowDateFieldInDialog(false);
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setShowDateFieldInDialog(false);
    if (!open) setEditingShift(null);
  };

  const handlePrevWeek = () => setCurrentWeek((d) => addDays(d, -7));
  const handleNextWeek = () => setCurrentWeek((d) => addDays(d, 7));
  const handleToday = () => setCurrentWeek(new Date());

  // Dépassements : overtime par employé pour la semaine affichée
  const { data: overtimeData } = useOvertime("week", weekStart, {
    enabled: !!effectiveCompanyId,
    companyId: isSuperuser ? effectiveCompanyId || undefined : undefined,
  });

  const overtimeByEmployeeId = useMemo(() => {
    const map = new Map<string, OvertimeEmployee>();
    for (const emp of overtimeData?.employees ?? []) {
      map.set(emp.employee.id, emp);
    }
    return map;
  }, [overtimeData]);

  // Modal "Voir les dépassements"
  const [overtimeModalEmployee, setOvertimeModalEmployee] =
    useState<OvertimeDialogEmployee | null>(null);

  // Duplication vers la semaine suivante
  // On ne duplique que les créneaux "PLANNED" (pas les "CANCELLED")
  const { mutate: createMutate, isPending: isDuplicating } = useCreateShifts();
  const { mutate: publishMutate, isPending: isPublishing } = usePublishShifts();
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateTargetWeek, setDuplicateTargetWeek] = useState<Date>(() => addDays(weekStart, 7));
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const plannedShifts = useMemo(
    () => shifts.filter((s) => s.status === "PLANNED"),
    [shifts]
  );

  const unpublishedCount = useMemo(
    () => shifts.filter((s) => !s.isPublished).length,
    [shifts]
  );

  const handleConfirmPublish = () => {
    publishMutate({
      from: format(weekStart, "yyyy-MM-dd"),
      to: format(addDays(weekEnd, 1), "yyyy-MM-dd"),
      companyId: isSuperuser ? effectiveCompanyId || undefined : undefined,
    });
    setPublishDialogOpen(false);
  };

  // Nom de l'entreprise courante (pour l'export PDF)
  //  - ADMIN : via user.company.name
  //  - SUPERUSER : depuis la liste des entreprises chargées
  const companyName = isSuperuser
    ? companies.find((c) => c.id === effectiveCompanyId)?.name ?? "Entreprise"
    : user?.company?.name ?? "Entreprise";

  // Export PDF du planning de la semaine affichée
  const handleExportPDF = () => {
    exportPlanningToPDF({
      companyName,
      weekStart,
      weekEnd,
      weekDays,
      employees,
      getCellShifts,
      getTotalMinutes: (employeeId: string) =>
        totalMinutesByEmployee.get(employeeId) ?? 0,
      formatDuration,
    });
  };

  const handleConfirmDuplicate = () => {
    const targetStart = startOfWeek(duplicateTargetWeek, { weekStartsOn: 1 }); // Lundi de la semaine cible
    const diffDays = Math.round(
      (targetStart.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)
    ); // Nombre de jours entre la semaine courante et la semaine cible


    const payload: ShiftCreateInput[] = plannedShifts.map((s) => ({
      employeeId: s.employeeId,
      startTime: addDays(new Date(s.startTime), diffDays).toISOString(),
      endTime: addDays(new Date(s.endTime), diffDays).toISOString(),
      breakMinutes: s.breakMinutes ?? undefined,
      note: s.note ?? null,
    }));
    createMutate({ shifts: payload });
    setDuplicateDialogOpen(false);
    setCurrentWeek((d) => addDays(d, diffDays));
  };



  // ─── Rendu ──────────────────────────────────────────────────────────────────

  // Un ADMIN sans entreprise associée : on bloque (ne devrait pas arriver)
  if (!isSuperuser && !user?.companyId) {
    return (
      <div className="p-6 text-muted-foreground">
        Aucune entreprise associée.
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête */}
      <div className="flex flex-col gap-3">

        {/* Ligne 1 : titre + navigation temporelle */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              <CalendarDays className="h-8 w-8 text-primary" />
              Planning
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Semaine du {format(weekStart, "d MMMM", { locale: fr })} au{" "}
              {format(weekEnd, "d MMMM yyyy", { locale: fr })}
            </p>
          </div>

          {/* Navigation semaine */}
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="icon" onClick={handlePrevWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={handleToday}>
              Aujourd'hui
            </Button>
            <Button variant="outline" size="icon" onClick={handleNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Ligne 2 : filtres à gauche, actions à droite */}
        <div className="flex flex-wrap items-center justify-between gap-2">

          {/* Filtres */}
          <div className="flex flex-wrap items-center gap-2">
            {isSuperuser && (
              <Select
                value={selectedCompanyId}
                onValueChange={setSelectedCompanyId}
              >
                <SelectTrigger className="w-[220px]">
                  <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Sélectionner une entreprise" />
                </SelectTrigger>
                <SelectContent>
                  {companies.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Aucune entreprise disponible
                    </div>
                  ) : (
                    companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Rechercher un employé..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-8 w-72"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Secondaires */}
            <Button
              variant="outline"
              onClick={handleExportPDF}
              disabled={!effectiveCompanyId || employees.length === 0}
              title={
                !effectiveCompanyId
                  ? "Sélectionnez une entreprise"
                  : employees.length === 0
                  ? "Aucun employé à exporter"
                  : "Télécharger le planning en PDF"
              }
            >
              <Download className="h-4 w-4 mr-1" />
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateTargetWeek(addDays(weekStart, 7));
                setDuplicateDialogOpen(true);
              }}
              disabled={
                !effectiveCompanyId ||
                plannedShifts.length === 0 ||
                isDuplicating
              }
              title={
                !effectiveCompanyId
                  ? "Sélectionnez une entreprise"
                  : plannedShifts.length === 0
                  ? "Aucun créneau à dupliquer"
                  : "Dupliquer vers la semaine suivante"
              }
            >
              <Copy className="h-4 w-4 mr-1" />
              Dupliquer
            </Button>

            {/* Séparateur visuel */}
            <div className="h-6 w-px bg-border" />

            {/* Publier — action critique */}
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(true)}
              disabled={!effectiveCompanyId || unpublishedCount === 0 || isPublishing}
              title={
                !effectiveCompanyId
                  ? "Sélectionnez une entreprise"
                  : unpublishedCount === 0
                  ? "Tous les créneaux sont déjà publiés"
                  : `Publier ${unpublishedCount} créneau(x) sur la pointeuse`
              }
              className={unpublishedCount > 0 ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50" : ""}
            >
              <Send className="h-4 w-4 mr-1" />
              Publier
              {unpublishedCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-xs font-bold">
                  {unpublishedCount}
                </span>
              )}
            </Button>

            {/* Primaire */}
            <Button
              onClick={() => {
                setEditingShift(null);
                setDialogEmployeeId(undefined);
                setDialogDate(format(new Date(), "yyyy-MM-dd"));
                setDialogOpen(true);
                setShowDateFieldInDialog(true);
              }}
              disabled={!effectiveCompanyId || employees.length === 0}
              title={
                !effectiveCompanyId
                  ? "Sélectionnez une entreprise"
                  : employees.length === 0
                  ? "Aucun employé dans cette entreprise"
                  : "Nouveau créneau"
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Nouveau créneau
            </Button>
          </div>
        </div>
      </div>

      {/* SUPERUSER sans entreprise choisie : placeholder explicite */}
      {isSuperuser && !effectiveCompanyId && (
        <Card className="p-10 text-center space-y-3">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-semibold">
            Sélectionnez une entreprise pour afficher son planning
          </div>
          <p className="text-sm text-muted-foreground">
            Utilisez le menu déroulant en haut à droite pour choisir
            l'entreprise à consulter.
          </p>
        </Card>
      )}

      {usersError && (
        <Card className="p-6 text-red-600">
          Erreur (employés) :{" "}
          {usersError instanceof Error
            ? usersError.message
            : "impossible de charger les employés"}
        </Card>
      )}

      {/* États de chargement / erreur (uniquement quand une entreprise est ciblée) */}
      {effectiveCompanyId && (usersLoading || shiftsLoading) && (
        <Card className="p-6">
          <Skeleton className="h-6 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </Card>
      )}

      {shiftsError && (
        <Card className="p-6 text-red-600">
          Erreur :{" "}
          {shiftsError instanceof Error
            ? shiftsError.message
            : "impossible de charger le planning"}
        </Card>
      )}

      {/* Tableau du planning */}
      {effectiveCompanyId && !usersLoading && !shiftsLoading && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1040px]">

              {/* En-tête */}
              <div className="grid grid-cols-[180px_repeat(7,minmax(120px,1fr))_100px] border-b bg-gray-50/80">
                <div className="px-3 py-2.5 border-r" />
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div
                      key={day.toISOString()}
                      className={`px-2 py-2 text-center border-r last:border-r-0 ${
                        isToday ? "bg-primary/10" : ""
                      }`}
                    >
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${
                        isToday ? "text-primary" : "text-muted-foreground"
                      }`}>
                        {format(day, "EEE", { locale: fr }).replace(".", "")}
                      </div>
                      <div className={`text-base font-bold leading-tight ${
                        isToday ? "text-primary" : "text-foreground"
                      }`}>
                        {format(day, "d")}
                      </div>
                      <div className="text-[10px] text-muted-foreground capitalize">
                        {format(day, "MMM", { locale: fr })}
                      </div>
                    </div>
                  );
                })}
                <div className="px-2 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide text-center border-l">
                  Total
                </div>
              </div>

              {employees.length === 0 && (
                <div className="p-6 text-center text-muted-foreground">
                  {search.trim()
                    ? `Aucun employé ne correspond à « ${search} »`
                    : "Aucun employé dans votre entreprise."}
                </div>
              )}

              {employees.map((employee, employeeIdx) => {
                const color = SHIFT_COLORS[employeeIdx % SHIFT_COLORS.length];
                const initials =
                  (employee.firstName?.[0] ?? "") + (employee.lastName?.[0] ?? "");

                return (
                  <div
                    key={employee.id}
                    className="grid grid-cols-[180px_repeat(7,minmax(120px,1fr))_100px] border-b last:border-b-0"
                  >
                    {/* Colonne employé */}
                    <div className="px-3 py-2 flex items-center gap-2 border-r">
                      <span className={`w-7 h-7 rounded-full ${color.bg} ${color.text} text-[10px] font-bold flex items-center justify-center shrink-0 ring-1 ${color.border}`}>
                        {initials}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-foreground truncate leading-tight">
                          {employee.firstName}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate leading-tight">
                          {employee.lastName}
                        </div>
                      </div>
                    </div>

                    {/* Cellules jours */}
                    {weekDays.map((day) => {
                      const cellShifts = getCellShifts(employee.id!, day);
                      const isToday = isSameDay(day, new Date());

                      return (
                        <div
                          key={day.toISOString()}
                          className={`p-1.5 border-r last:border-r-0 min-h-[80px] flex flex-col gap-1.5 ${
                            isToday ? "bg-primary/5" : ""
                          }`}
                        >
                          {cellShifts.map((shift) => {
                            const isCancelled = shift.status === "CANCELLED";
                            const leaveStyle = getLeaveStyle(shift.note);
                            const mins = getShiftMinutes(shift);

                            if (isCancelled) {
                              return (
                                <button
                                  key={shift.id}
                                  type="button"
                                  onClick={() => handleOpenEditDialog(shift)}
                                  title="Créneau annulé — cliquer pour modifier"
                                  className="w-full rounded-md px-2 py-1.5 text-[11px] border bg-gray-50 text-gray-400 border-dashed border-gray-300 line-through text-center hover:bg-gray-100 transition-colors"
                                >
                                  {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
                                </button>
                              );
                            }

                            if (leaveStyle) {
                              return (
                                <button
                                  key={shift.id}
                                  type="button"
                                  onClick={() => handleOpenEditDialog(shift)}
                                  title="Modifier"
                                  className={`w-full rounded-md px-2 py-1.5 text-[11px] border ${leaveStyle} hover:brightness-95 transition-all`}
                                >
                                  <div className="font-semibold text-center leading-tight">{shift.note}</div>
                                  {mins > 0 && (
                                    <div className="text-center font-bold mt-0.5">{formatDuration(mins)}</div>
                                  )}
                                </button>
                              );
                            }

                            return (
                              <button
                                key={shift.id}
                                type="button"
                                onClick={() => handleOpenEditDialog(shift)}
                                title="Modifier"
                                className={`w-full rounded-md px-2 py-1.5 text-[11px] border ${color.bg} ${color.text} ${color.border} ${color.hover} transition-colors relative`}
                              >
                                <div className="font-semibold text-center tabular-nums leading-tight">
                                  {formatTime(shift.startTime)} – {formatTime(shift.endTime)}
                                </div>
                                <div className="text-center font-bold mt-0.5">
                                  {formatDuration(mins)}
                                </div>
                                {shift.note && (
                                  <div className="text-center opacity-70 truncate leading-tight mt-0.5">
                                    {shift.note}
                                  </div>
                                )}
                                {!shift.isPublished && (
                                  <span className="absolute top-0.5 right-0.5 text-[8px] uppercase tracking-wide opacity-60 font-semibold">
                                    ●
                                  </span>
                                )}
                              </button>
                            );
                          })}

                          <button
                            type="button"
                            onClick={() => handleOpenCreateDialog(employee.id!, day)}
                            className="mt-auto flex items-center justify-center gap-1 rounded border border-dashed text-xs text-muted-foreground hover:text-primary hover:border-primary py-1 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                            Ajouter
                          </button>
                        </div>
                      );
                    })}

                    {/* Total semaine */}
                    {(() => {
                      const overtime = overtimeByEmployeeId.get(employee.id!);
                      const hasOvertime = overtime?.status === "over";
                      return (
                        <div className="px-2 py-2 flex flex-col items-center justify-center gap-1 border-l bg-gray-50/60">
                          <span className="font-bold text-sm tabular-nums">
                            {formatDuration(totalMinutesByEmployee.get(employee.id!) ?? 0)}
                          </span>
                          {hasOvertime && (
                            <button
                              type="button"
                              onClick={() => setOvertimeModalEmployee({
                                id: employee.id!,
                                name: `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim(),
                              })}
                              title="Voir les dépassements"
                              className="flex items-center gap-1 cursor-pointer rounded px-1.5 py-0.5 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors"
                            >
                              <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                              {overtime.overtimeFormatted}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      <EmployeeOvertimeDialog
        employee={overtimeModalEmployee}
        open={!!overtimeModalEmployee}
        onOpenChange={(open: boolean) => { if (!open) setOvertimeModalEmployee(null); }}
        from={format(weekStart, "yyyy-MM-dd")}
        to={format(weekEnd, "yyyy-MM-dd")}
      />

      <ShiftFormDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        employees={employees}
        shift={editingShift}
        defaultDate={dialogDate}
        defaultEmployeeId={dialogEmployeeId}
        showDateField={showDateFieldInDialog}
        />

      {/* Confirmation de duplication de semaine */}
      <AlertDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dupliquer la semaine ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Vous êtes sur le point de copier{" "}
                  <span className="font-semibold text-foreground">
                    {plannedShifts.length} créneau(x)
                  </span>{" "}
                  de la semaine actuelle vers la semaine suivante.
                </p>

                <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Semaine source :</span>
                    <span>
                      {format(weekStart, "d MMM", { locale: fr })} –{" "}
                      {format(weekEnd, "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Copy className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Semaine cible :</span>
                    </div>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                          {format(
                            startOfWeek(duplicateTargetWeek, { weekStartsOn: 1 }),
                            "d MMM",
                            { locale: fr }
                          )}{" "}
                          –{" "}
                          {format(
                            endOfWeek(duplicateTargetWeek, { weekStartsOn: 1 }),
                            "d MMM yyyy",
                            { locale: fr }
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={startOfWeek(duplicateTargetWeek, { weekStartsOn: 1 })}
                          onSelect={(date) => {
                            if (date) {
                              setDuplicateTargetWeek(date);
                              setCalendarOpen(false);
                            }
                          }}
                          locale={fr}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Les créneaux déjà présents sur la semaine cible (chevauchement
                  avec un créneau existant) seront automatiquement ignorés. Les
                  créneaux annulés ne sont pas dupliqués.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicating}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDuplicate}
              disabled={isDuplicating}
            >
              {isDuplicating ? "Duplication..." : "Dupliquer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation de publication */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publier le planning ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Vous êtes sur le point de publier{" "}
                  <span className="font-semibold text-foreground">
                    {unpublishedCount} créneau(x) en brouillon
                  </span>{" "}
                  sur la pointeuse. Ils seront visibles immédiatement par les employés.
                </p>

                <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Semaine :</span>
                    <span>
                      {format(weekStart, "d MMM", { locale: fr })} –{" "}
                      {format(weekEnd, "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Entreprise :</span>
                    <span>{companyName}</span>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Cette action est immédiate. Les créneaux déjà publiés ne sont pas affectés.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPublishing}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPublish}
              disabled={isPublishing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isPublishing ? "Publication..." : "Publier"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
